<template>
  <div :class="allClasses">
    <ENotificationBanner v-if="featureEnabled('notifications')" />
    <header class="main-header__content" aria-label="Main header">
      <div class="main-header__navigation">
        <div v-if="featureEnabled('logo')" class="main-header__logo">
          <slot name="logo">
            <ELogo
              :href="logoUrl"
              :title="logoTitle"
              :padding="false"
              class="main-header__logo-link"
              data-test-id="main-header-logo"
            />
          </slot>
        </div>
        <ESearchForm
          v-if="searchEnabled"
          class="main-header__search"
          :aria-label="$t('common.search.searchbox.label')"
          :placeholder="$t('common.search.searchbox.placeholder.desktop')"
          :market="bingMarketCode"
          :action="searchUrl"
          :autocomplete-url="autocompleteUrl"
          :disable-overlay="searchDisableOverlay"
          :has-auto-focus="searchAutoFocus"
          :hidden-inputs="searchHiddenInputs"
          prefill-query
          variant="compact"
          data-test-id="main-header-search-form"
        />
        <transition name="fade-cta">
          <div v-if="visibleCta" class="main-header__install-cta">
            <ECta
              :show-share="showShareCta"
              :custom-cta-text="ctaText"
              :custom-track-id="location"
              size="m"
              data-test-id="main-header-install-cta"
            />
          </div>
        </transition>
        <EPersonalCounter
          v-if="featureEnabled('tree-counter') && personalCounterFetched"
          :allow-impact-redirect="personalCounterImpactRedirect"
          :should-increment="personalCounterIncrement"
          class="main-header__tree-counter"
        />
        <div
          v-else
          class="main-header__counter-placeholder"
        />
        <ENotifications
          v-if="featureEnabled('notifications')"
          class="main-header__notifications"
          :blog-url="blogUrl"
          :read-more-label="$t('common.header.notifications.readmore')"
        />
        <EMainNav
          class="main-header__nav"
          panel-side="right"
        />
      </div>
      <div v-if="$slots.navigation" class="main-header__search-navigation">
        <slot name="navigation" />
      </div>
    </header>
    <slot name="filters" />
  </div>
</template>

<script>
// TODO: translate aria-label on .main-header__content
import { mapState } from 'vuex';

import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import { headerHiddenBrowsers } from '~~/common/js/constants/browsers';
import { HEADER_INSTALL_CTA_DISMISSED } from '~~/common/js/constants/cookies';
import { getBlogURL } from '~~/common/js/universal/url-utils';
import classes from '~~/common/vue/mixins/classes';
import loginMixin from '~~/common/vue/mixins/login';

import ELogo from '~~/common/vue/components/logo';
import ECta from '~~/common/vue/components/cta';
import EMainNav from '~~/common/vue/components/main-nav';
import ENotifications from '~~/common/vue/components/notifications';
import ENotificationBanner from '~~/common/vue/components/notifications/banner';
import ESearchForm from '~~/common/vue/components/search-form';
import EPersonalCounter from '~~/common/vue/components/personal-counter';

export const headerLocations = ['serp', 'index'];
export const headerFeatures = [
  'logo',
  'search',
  'notifications',
  'tree-counter',
  'install-cta',
];

// To disable search feature in future for ios, add 'ecosia ios'
export const disableSearchBrowsers = [];

// eslint-disable-next-line arrow-body-style
const headerFeatureValidator = (features) => {
  return features.every((feature) => headerFeatures.includes(feature));
};

const headerLocationValidator = (location) => headerLocations.includes(location);

export default {
  name: 'EMainHeader',
  components: {
    ELogo,
    ECta,
    EMainNav,
    ENotifications,
    ENotificationBanner,
    ESearchForm,
    EPersonalCounter,
  },
  mixins: [classes, loginMixin],
  props: {
    /**
     * A list of features which will be disabled. All enabled by default.
     * One of `logo`, `search`, `notifications`, `tree-counter`, `install-cta`.
     */
    disableFeatures: {
      type: Array,
      default: () => [],
      validator: headerFeatureValidator,
    },
    border: {
      type: Boolean,
      default: true,
      cssClass: true,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    showShareCta: {
      type: Boolean,
      default: false,
    },
    ctaText: {
      type: String,
      default: '',
    },
    searchAutocomplete: {
      type: Boolean,
      default: true,
    },
    searchAutoFocus: {
      type: Boolean,
      default: false,
    },
    searchDisableOverlay: {
      type: Boolean,
      default: false,
    },
    searchHiddenInputs: {
      type: Object,
      default: () => ({}),
    },
    logoUrl: {
      type: String,
      default: '/',
    },
    logoTitle: {
      type: String,
      default: '',
    },
    location: {
      type: String,
      default: 'serp',
      validator: headerLocationValidator,
    },
    personalCounterIncrement: {
      type: Boolean,
      default: false,
    },
    personalCounterImpactRedirect: {
      type: Boolean,
      default: false,
    },
    transparent: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  data: () => ({
    shouldShowCta: false,
  }),
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', ['bingMarketCode', 'browser', 'treeCount']),
    searchUrl() {
      const { searchUrl } = this.$config;
      if (searchUrl) {
        return searchUrl;
      }
      switch (this.$route.path) {
        case '/':
        case '/settings':
          return '/search';
        default:
          return this.$route.path;
      }
    },
    autocompleteUrl() {
      return this.searchAutocomplete && this.$config.autocompleteUrl ? this.$config.autocompleteUrl : '';
    },
    allClasses() {
      const allClasses = [...this.classes];
      if (!this.$slots.searchNavigation) {
        allClasses.push('main-header--with-verticals');
      }
      if (this.isSticky) {
        allClasses.push('main-header--sticky');
      }
      return allClasses;
    },
    blogUrl() {
      return getBlogURL(this.locale);
    },
    searchEnabled() {
      return disableSearchBrowsers.includes(this.browser) ?
        false :
        this.featureEnabled('search');
    },
    visibleCta() {
      return this.shouldShowCta && this.featureEnabled('install-cta');
    },
  },
  watch: {
    filtersShown(value) {
      if (value) {
        // defer to next event loop cycle, because Vue won't have
        // rendered the filters at this point. `$nextTick` didn't
        // seem to work in this case.
        setTimeout(() => {
          const filters = document.getElementById('search-filters');
          if (filters) {
            focusFocusableChild(filters);
          }
        }, 0);
      }
    },
  },
  mounted() {
    const serpCtaDismissed = this.location === 'serp' && this.$cookieManager.get(HEADER_INSTALL_CTA_DISMISSED);
    this.shouldShowCta = !headerHiddenBrowsers.includes(this.browser) && !serpCtaDismissed;

    if (this.featureEnabled('tree-counter')) {
      this.initHeimdallLogin();
    }
  },
  methods: {
    featureEnabled(feature) {
      return !this.disableFeatures.includes(feature);
    },
  },
};
</script>

<style lang="scss" scoped>
.main-header__logo {
  align-self: center;
  margin-left: auto;
  order: 2;

  @include tablet {
    align-self: auto;
  }

  @include desktop {
    margin-left: 0;
    order: unset;
  }
}

.main-header__logo-link {
  display: flex;
  height: 20px;
  width: 70px;
  position: relative;

  @include desktop {
    margin-right: $space-1l;
  }
}

.main-header__search {
  margin-top: $space-s;
  order: 4; // after tree counter, TBA
  width: 100%;

  @include desktop {
    flex-shrink: 1;
    margin-top: 0;
    max-width: $page-container-width-desktop;
    order: unset;

    .main-header__logo + & {
      margin: 0 $space-l 0 0;
    }
  }
}

.main-header__nav {
  order: 3;
  margin-left: auto;

  @include desktop {
    margin-left: unset;
  }
}
// bypassing stylelint here because the block it's complaining
// about is only a placeholder and will be removed
// stylelint-disable-next-line no-duplicate-selectors
.main-header__notifications {
  display: none;
  flex-shrink: 0;

  @include desktop {
    display: block;
    margin-right: $space-1s;
    order: unset;
  }
}

.main-header__tree-counter,
.main-header__counter-placeholder {
  flex-shrink: 0;
  margin-right: 0;
  order: 1;

  @include desktop {
    margin-right: $space-1s;
    margin-left: auto;
    order: unset;
  }
}

.main-header__counter-placeholder {
  width: 45px;
}

.main-header__install-cta {
  display: none;
  transition: opacity $timing-m;

  @include desktop {
    display: block;
    margin: 0 $space-2l 0 $space-3l;
  }
}

.main-header__content {
  padding: 0 $space-m $space-m;
  position: relative;
  z-index: $z-index-1l;

  & > * {
    flex-shrink: 0;
  }

  .main-header--with-verticals & {
    padding-bottom: 0;
  }

  .main-header--border & {
    @include divider('bottom');
  }

  .main-header:not(.main-header--transparent) & {
    background: var(--color-background-primary);
  }

  @include tablet {
    padding: 0 $space-s $space-m;
  }

  @include desktop {
    padding: 0 $space-1l;
  }
}

.main-header--sticky {
  position: sticky;
  top: 0;
  z-index: $z-index-1l;
}

.main-header__navigation {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-top: $space-1s;
  width: 100%;

  // Applies when navbar is not used, e.g. Settings
  &:last-child {
    padding-bottom: $space-m;
  }

  @include desktop {
    flex-wrap: nowrap;
    padding-top: $space-m;
  }
}

.main-header__search-navigation {
  margin: $space-2s -1 * $space-m 0 -1 * $space-m;
  width: auto;

  @include desktop {
    margin: $space-1s 0 0;
    padding-left: $page-container-left-space-desktop - $space-1s;
    width: 100%;
  }
}

.fade-cta-leave-active {
  opacity: 0;
}
</style>
